import { useState, type ReactNode } from "react";
import { Link, useNavigate, useSearchParams } from "react-router";
import { css } from "styled-system/css";
import type { TeamDetailResponse, TeamSummary } from "@aura/shared";
import { Banner, btn, ed, field, tbl } from "~/components/editorial";
import { fmtNum } from "~/lib/format";
import { displayKey, inferenceSummary } from "../model";
import { useInferTeams, usePinMember, useUpdateTeam } from "../mutations";

// Admin controls on the Teams pages. Callers render these only for admins.

function errorText(e: unknown): string {
  return e instanceof Error ? e.message : String(e);
}

/** "Re-run inference" with its result or error as a banner; `children` sit beside it. */
export function InferControl({ children }: { children?: ReactNode }) {
  const infer = useInferTeams();
  const [dismissed, setDismissed] = useState(false);
  return (
    <div className={styles.block}>
      <div className={styles.row}>
        <span className={styles.label}>Admin</span>
        <button
          type="button"
          className={btn.sentenceOutline}
          disabled={infer.isPending}
          onClick={() => {
            setDismissed(false);
            infer.mutate();
          }}
        >
          {infer.isPending ? "Working out teams…" : "Re-run inference"}
        </button>
        {children}
      </div>
      {!dismissed && infer.data && (
        <Banner onDismiss={() => setDismissed(true)}>{inferenceSummary(infer.data)}</Banner>
      )}
      {!dismissed && infer.error && (
        <Banner tone="caution" onDismiss={() => setDismissed(true)}>
          Couldn't re-run inference: {errorText(infer.error)}
        </Banner>
      )}
    </div>
  );
}

/** Hidden teams, listed apart from the comparisons, each with an Unhide action. */
export function HiddenTeams({ teams }: { teams: TeamSummary[] }) {
  const update = useUpdateTeam();
  const [searchParams] = useSearchParams();
  const suffix = searchParams.toString() ? `?${searchParams.toString()}` : "";
  return (
    <section className={styles.hidden} aria-label="Hidden teams">
      <div className={ed.sect}>Hidden</div>
      <p className={ed.prose}>
        {teams.length === 0
          ? "No teams are hidden."
          : "Left out of every comparison on this page. They keep their people. Only admins see this list."}
      </p>
      {update.error && (
        <Banner tone="caution" onDismiss={() => update.reset()}>
          Couldn't unhide the team: {errorText(update.error)}
        </Banner>
      )}
      {teams.length > 0 && (
        <div className={tbl.scrollPlain}>
          <table className={tbl.table}>
            <thead>
              <tr>
                <th>Hidden team</th>
                <th data-num>People</th>
                <th data-num>PRs merged</th>
                <th aria-label="Action" />
              </tr>
            </thead>
            <tbody>
              {teams.map((t) => (
                <tr key={t.id}>
                  <td style={{ whiteSpace: "nowrap" }}>
                    <Link to={`/teams/${t.id}${suffix}`}>{t.name}</Link>
                    {displayKey(t.key) && t.key !== t.name && (
                      <span className={tbl.sub}>{t.key}</span>
                    )}
                    <span className={tbl.flag} data-tone="quiet">
                      Hidden
                    </span>
                  </td>
                  <td data-num>{fmtNum(t.current.headcount)}</td>
                  <td data-num>{fmtNum(t.current.prsMerged)}</td>
                  <td style={{ textAlign: "right" }}>
                    <button
                      type="button"
                      className={btn.sentenceOutline}
                      disabled={update.isPending}
                      aria-label={`Unhide ${t.name}`}
                      onClick={() => update.mutate({ teamId: t.id, input: { hidden: false } })}
                    >
                      Unhide
                    </button>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </section>
  );
}

/** Rename and hide (or unhide) for one team. */
export function TeamAdminBar({ team }: { team: TeamDetailResponse["team"] }) {
  const update = useUpdateTeam();
  const navigate = useNavigate();
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(team.name);
  const [confirmHide, setConfirmHide] = useState(false);
  const trimmed = name.trim();

  return (
    <div className={styles.block}>
      <div className={styles.row}>
        <span className={styles.label}>Admin</span>
        {editing ? (
          <form
            className={styles.row}
            onSubmit={(e) => {
              e.preventDefault();
              if (!trimmed || trimmed === team.name) return setEditing(false);
              update.mutate(
                { teamId: team.id, input: { name: trimmed } },
                { onSuccess: () => setEditing(false) },
              );
            }}
          >
            <input
              className={field.input}
              style={{ width: 220 }}
              value={name}
              onChange={(e) => setName(e.target.value)}
              aria-label="Team name"
              maxLength={80}
              autoFocus
            />
            <button type="submit" className={btn.sentence} disabled={update.isPending || !trimmed}>
              Save name
            </button>
            <button
              type="button"
              className={btn.sentenceOutline}
              onClick={() => {
                setName(team.name);
                setEditing(false);
              }}
            >
              Cancel
            </button>
          </form>
        ) : (
          <button type="button" className={btn.sentenceOutline} onClick={() => setEditing(true)}>
            Rename team
          </button>
        )}
        {team.hidden ? (
          <button
            type="button"
            className={btn.sentenceOutline}
            disabled={update.isPending}
            onClick={() => update.mutate({ teamId: team.id, input: { hidden: false } })}
          >
            Unhide team
          </button>
        ) : confirmHide ? (
          <>
            <button
              type="button"
              className={btn.sentence}
              disabled={update.isPending}
              onClick={() =>
                update.mutate(
                  { teamId: team.id, input: { hidden: true } },
                  { onSuccess: () => navigate("/teams") },
                )
              }
            >
              Hide {team.name} from comparisons
            </button>
            <button type="button" className={btn.sentenceOutline} onClick={() => setConfirmHide(false)}>
              Keep it
            </button>
          </>
        ) : (
          <button type="button" className={btn.sentenceOutline} onClick={() => setConfirmHide(true)}>
            Hide team
          </button>
        )}
      </div>
      {update.error && (
        <Banner tone="caution" onDismiss={() => update.reset()}>
          Couldn't update the team: {errorText(update.error)}
        </Banner>
      )}
    </div>
  );
}

type Member = TeamDetailResponse["members"][number];
type PinMutation = ReturnType<typeof usePinMember>;

/** Move, lead and pin controls for one roster row. */
export function MemberActions({
  teamId,
  member,
  otherTeams,
  pin,
}: {
  teamId: string;
  member: Member;
  otherTeams: Array<{ id: string; name: string }>;
  pin: PinMutation;
}) {
  const [target, setTarget] = useState("");
  const busy = pin.isPending;
  const who = member.displayName;
  return (
    <div className={styles.actions}>
      {otherTeams.length > 0 && (
        <>
          <select
            className={field.select}
            value={target}
            onChange={(e) => setTarget(e.target.value)}
            aria-label={`Move ${who} to team`}
          >
            <option value="">Move to…</option>
            {otherTeams.map((t) => (
              <option key={t.id} value={t.id}>
                {t.name}
              </option>
            ))}
          </select>
          <button
            type="button"
            className={btn.sentenceOutline}
            disabled={busy || !target}
            title="Pins them to that team and drops their inferred place here. A pin set by hand elsewhere stays."
            onClick={() =>
              pin.mutate(
                { teamId: target, developerId: member.developerId, input: { pinned: true } },
                { onSuccess: () => setTarget("") },
              )
            }
          >
            Move
          </button>
        </>
      )}
      <button
        type="button"
        className={btn.sentenceOutline}
        disabled={busy}
        aria-label={member.isLead ? `Remove ${who} as lead` : `Make ${who} lead`}
        onClick={() =>
          pin.mutate({
            teamId,
            developerId: member.developerId,
            input: { pinned: true, isLead: !member.isLead },
          })
        }
      >
        {member.isLead ? "Remove lead" : "Make lead"}
      </button>
      {member.source === "pinned" && (
        <>
          <button
            type="button"
            className={btn.sentenceOutline}
            disabled={busy}
            aria-label={`Release pin on ${who}`}
            aria-describedby={`release-hint-${member.developerId}`}
            onClick={() =>
              pin.mutate({ teamId, developerId: member.developerId, input: { pinned: false } })
            }
          >
            Release pin
          </button>
          <span id={`release-hint-${member.developerId}`} className={styles.hint}>
            Removes lead status; the next inference places them again.
          </span>
        </>
      )}
    </div>
  );
}

const styles = {
  block: css({ mt: "14px", mb: "18px", display: "flex", flexDirection: "column", gap: "8px" }),
  row: css({ display: "flex", flexWrap: "wrap", alignItems: "center", gap: "8px" }),
  label: css({
    fontFamily: "sans",
    fontSize: "10.5px",
    fontWeight: 700,
    letterSpacing: "0.08em",
    textTransform: "uppercase",
    color: "ed.ink3",
  }),
  actions: css({
    display: "flex",
    flexWrap: "wrap",
    maxWidth: "360px",
    alignItems: "center",
    gap: "6px",
    "& button": { whiteSpace: "nowrap" },
  }),
  hint: css({
    fontFamily: "sans",
    fontSize: "10.5px",
    lineHeight: 1.3,
    color: "ed.ink3",
    // Its own line under the buttons, so the row stays inside the table.
    flexBasis: "100%",
    whiteSpace: "normal",
  }),
  hidden: css({ mb: "8px" }),
};
